import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

// Mirrors REQUIRED_PROFILE in backend/consultants.py and
// STAFF_REQUIRED_PROFILE in backend/server.py. Doctors and contractors are
// paid the same way, so they are asked for the same things.
export const PROFILE_FIELDS = [
  ["phone", "Phone"],
  ["pan", "PAN"],
  ["address", "Address"],
  ["emergency_contact_name", "Emergency contact name"],
  ["emergency_contact_phone", "Emergency contact phone"],
  ["account_holder_name", "Account holder name"],
  ["bank_name", "Bank name"],
  ["bank_account", "Account number"],
  ["bank_ifsc", "IFSC"],
];

export const PROFILE_REQUIRED = PROFILE_FIELDS.map(([k]) => k);

export const profileMissing = (p) =>
  PROFILE_REQUIRED.filter((f) => !String((p || {})[f] || "").trim());

/**
 * A gate, not a reminder. There is no close button, Escape does nothing and
 * clicking outside does nothing, so the portal cannot be reached until every
 * field is in. Deliberate: we cannot pay someone whose IFSC we do not have,
 * and asking politely has not worked.
 *
 * `endpoint` is the caller's own PATCH route, which is what keeps this usable
 * by both the doctor portal and the contractor portal without either knowing
 * about the other.
 */
export default function ProfileGate({ me, endpoint, onSaved }) {
  const [form, setForm] = useState(() => {
    const f = {};
    PROFILE_REQUIRED.forEach((k) => { f[k] = (me || {})[k] || ""; });
    return f;
  });
  const [saving, setSaving] = useState(false);

  // useState's initialiser only runs on the first render, and on that
  // render the parent is still fetching, so `me` is null and every box
  // starts empty. Fill in whatever is already on file once it arrives,
  // without clobbering anything the person has begun typing.
  useEffect(() => {
    if (!me) return;
    setForm((prev) => {
      const next = { ...prev };
      PROFILE_REQUIRED.forEach((k) => {
        if (!String(next[k] || "").trim()) next[k] = me[k] || "";
      });
      return next;
    });
  }, [me]);

  if (!me || profileMissing(me).length === 0) return null;

  const stillMissing = PROFILE_REQUIRED.filter((f) => !String(form[f] || "").trim());

  const save = async (e) => {
    e.preventDefault();
    if (stillMissing.length) {
      const labels = stillMissing.map(
        (f) => (PROFILE_FIELDS.find(([k]) => k === f) || [f, f])[1]);
      toast.error("Still needed: " + labels.join(", "));
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(endpoint, form);
      toast.success("Thank you - your details are saved.");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open>
      <DialogContent
        className="max-w-lg [&>button]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="profile-gate">
        <DialogHeader>
          <DialogTitle>Please complete your details</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-zinc-600 -mt-1">
          We need these to pay you and to reach someone in an emergency. Your
          statements are here waiting as soon as you save.
        </p>
        <form onSubmit={save} className="grid sm:grid-cols-2 gap-3 pt-1">
          {PROFILE_FIELDS.map(([k, label]) => (
            <div key={k} className={k === "address" ? "sm:col-span-2" : ""}>
              <label className="text-xs text-zinc-500">{label}</label>
              <input
                className="w-full border border-zinc-300 rounded px-2 py-1.5 text-sm"
                value={form[k] || ""}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                data-testid={`gate-${k}`} />
            </div>
          ))}
          <div className="sm:col-span-2 flex items-center justify-between pt-1">
            <span className="text-[11px] text-zinc-400">
              {stillMissing.length
                ? `${stillMissing.length} still to fill in`
                : "All set"}
            </span>
            <Button type="submit" disabled={saving}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="gate-save">
              {saving ? "Saving..." : "Save and continue"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
